// Tournament types — единая модель данных для командного режима.

import type { UnlockType } from './content';

export type MinigameId = 'wai' | 'describe' | 'act' | 'emoji';

export interface TournamentTeam {
  id: string;             // 't1', 't2', 't3', 't4'
  name: string;
  emoji: string;
  color: 'pink' | 'blue' | 'green' | 'gold';
  score: number;
  playerIds: string[];
}

/** Карточки одного пула — всегда массив строк (для всех 4 мини-игр). */
export interface MinigamePoolEntry {
  /** Сколько уровней склеено в этот пул (для статистики/отладки). */
  levelIds: string[];
  cards: string[];
  /** Курсор внутри shuffle'нутого массива cards. */
  cursor: number;
}

export type MinigamePool = Partial<Record<MinigameId, MinigamePoolEntry>>;

export interface TournamentSettings {
  scoreTarget: 5 | 10 | 15 | 20;
  turnDurationSec: 0 | 30 | 60 | 90;
  punishment: string;
  enabledMinigames: MinigameId[];
  /** Какие unlock-категории учитывать при сборе пула карточек. По умолчанию — все. */
  enabledCategories: UnlockType[];   // ['free','coin','premium','invite']
}

export type TournamentStatus =
  | 'lobby'
  | 'rolling'
  | 'playing'
  | 'finished';

export interface TournamentRoundSnapshot {
  teams: TournamentTeam[];
  currentTeamIndex: number;
  roundNumber: number;
  isSuddenDeath: boolean;
  suddenDeathRound: number;
  settings: TournamentSettings;
  finishedThisRound: string[];
  currentPlayerInTeam: Record<string, number>;
}

export interface TournamentState {
  teams: TournamentTeam[];
  settings: TournamentSettings;
  pool: MinigamePool;
  currentTeamIndex: number;
  roundNumber: number;
  currentMinigame: MinigameId | null;
  currentPlayerInTeam: Record<string, number>;
  status: TournamentStatus;
  isSuddenDeath: boolean;
  suddenDeathModalShown: boolean;
  suddenDeathRound: number;
  winnerTeamId: string | null;
  finishedThisRound: string[];
  lastRoundSnapshot: TournamentRoundSnapshot | null;
}

export const TEAM_DEFAULTS: { emoji: string; color: TournamentTeam['color'] }[] = [
  { emoji: '🦊', color: 'pink' },
  { emoji: '🐻', color: 'blue' },
  { emoji: '🦅', color: 'green' },
  { emoji: '🦁', color: 'gold' },
];

/** Список emoji для cycle-кнопки команды (по аналогии с EMOJIS в GameSetup). */
export const TEAM_EMOJI_POOL = [
  '🦊','🐻','🦅','🦁','🐯','🐺','🐲','🦄',
  '🐼','🐸','🐮','🐵','🦖','🐙','🦋','🦉',
  '🔥','⚡','💎','👑','🏴‍☠️','🚀','🌟','💀',
];

export const TEAM_COLORS: Record<TournamentTeam['color'], { bg: string; border: string; accent: string; bar: string }> = {
  pink:  { bg: 'bg-[rgba(255,45,107,0.10)]',  border: 'border-neon-pink/40',  accent: 'text-neon-pink',  bar: 'bg-grad-primary' },
  blue:  { bg: 'bg-[rgba(45,107,255,0.10)]',  border: 'border-neon-blue/40',  accent: 'text-neon-blue',  bar: 'bg-grad-blue' },
  green: { bg: 'bg-[rgba(57,255,20,0.10)]',   border: 'border-neon-green/40', accent: 'text-neon-green', bar: 'bg-grad-green' },
  gold:  { bg: 'bg-[rgba(255,215,0,0.10)]',   border: 'border-neon-gold/40',  accent: 'text-neon-gold',  bar: 'bg-grad-gold' },
};

/** Пользовательские пресеты названий команд (random на init). */
export const TEAM_NAME_PRESETS: { en: string[]; ru: string[] } = {
  en: ['Red Foxes','Blue Bears','Green Eagles','Golden Lions','Wild Tigers','Fire Wolves','Dragons','Unicorns'],
  ru: ['Лисы','Медведи','Орлы','Львы','Тигры','Волки','Драконы','Единороги'],
};
